import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Sentry } from '@/lib/sentry';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  level?: 'app' | 'page' | 'section';
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });

    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const level = this.props.level || 'page';

    if (level === 'app') {
      return <AppLevelError error={this.state.error} onReset={this.handleReset} />;
    }

    if (level === 'section') {
      return <SectionLevelError error={this.state.error} onReset={this.handleReset} />;
    }

    return <PageLevelError error={this.state.error} onReset={this.handleReset} />;
  }
}

// Full-page error for app-level crashes
function AppLevelError({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-sunken px-4">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-bad-soft">
          <AlertTriangle className="h-8 w-8 text-bad" />
        </div>
        <h1 className="mb-2 text-h3 font-bold text-ink">Something went wrong</h1>
        <p className="mb-6 text-small text-muted">
          An unexpected error occurred. Please try refreshing the page.
        </p>
        {import.meta.env.DEV && error && (
          <pre className="mb-6 overflow-auto rounded-lg bg-inverse p-4 text-left text-caption text-bad">
            {error.message}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        )}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-lg bg-[#09090B] px-4 py-2 text-small font-medium text-white hover:bg-raised"
          >
            <RefreshCw className="h-4 w-4" />
            Reload Page
          </button>
          <button
            onClick={() => { window.location.href = '/home'; }}
            className="inline-flex items-center gap-2 rounded-lg border border-line-strong bg-surface px-4 py-2 text-small font-medium text-strong hover:bg-sunken"
          >
            <Home className="h-4 w-4" />
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

// Page-level error within the dashboard layout
function PageLevelError({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-bad-soft">
        <AlertTriangle className="h-6 w-6 text-bad" />
      </div>
      <h2 className="mb-2 text-title font-semibold text-ink">Page Error</h2>
      <p className="mb-4 max-w-sm text-center text-small text-muted">
        This page encountered an error. Try refreshing or navigate to another page.
      </p>
      {import.meta.env.DEV && error && (
        <pre className="mb-4 max-w-lg overflow-auto rounded-lg bg-inverse p-3 text-caption text-bad">
          {error.message}
        </pre>
      )}
      <div className="flex gap-2">
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 rounded-lg bg-[#09090B] px-3 py-2 text-small font-medium text-white hover:bg-raised"
        >
          <RefreshCw className="h-4 w-4" />
          Try Again
        </button>
      </div>
    </div>
  );
}

// Section-level error for component sections
function SectionLevelError({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div className="rounded-lg border border-bad-line bg-bad-soft p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-bad" />
        <div className="flex-1">
          <p className="text-small font-medium text-bad">Failed to load this section</p>
          {import.meta.env.DEV && error && (
            <p className="mt-1 text-caption text-bad">{error.message}</p>
          )}
          <button
            onClick={onReset}
            className="mt-2 text-small font-medium text-bad underline hover:text-bad"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}
